import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { fetchPaymentStatus, startCashfreePayment } from "../lib/cashfree";
import { shopError } from "../lib/shop";
import { PageLoader } from "../components/LoadingRing";

const MAX_POLLS = 14;

export default function PaymentReturn() {
    const [params] = useSearchParams();
    const orderNumber = params.get("order") || sessionStorage.getItem("rhm_pending_order") || "";
    const navigate = useNavigate();
    const { clear } = useCart();
    const [state, setState] = useState("checking");
    const tries = useRef(0);
    const [retrying, setRetrying] = useState(false);

    const check = useCallback(async () => {
        if (!orderNumber) { setState("failed"); return; }
        try {
            const data = await fetchPaymentStatus(orderNumber);
            if (data.payment_status === "paid") {
                clear();
                sessionStorage.removeItem("rhm_pending_order");
                setState("paid");
                setTimeout(() => navigate(`/order/${orderNumber}`, { replace: true }), 1400);
                return;
            }
            if (data.payment_status === "failed") { setState("failed"); return; }
            tries.current += 1;
            setState(tries.current >= MAX_POLLS ? "pending" : "checking");
            if (tries.current < MAX_POLLS) setTimeout(check, 2500);
        } catch (e) {
            setState("failed");
        }
    }, [orderNumber, clear, navigate]);

    useEffect(() => { check(); }, [check]);

    const retry = async () => {
        setRetrying(true);
        try {
            await startCashfreePayment(orderNumber);
        } catch (e) {
            toast.error(shopError(e));
            setRetrying(false);
        }
    };

    return (
        <div className="pt-32 pb-24 min-h-[70vh]" data-testid="payment-return-page">
            <div className="mx-auto max-w-xl px-6 text-center">
                {state === "checking" && (
                    <div data-testid="payment-checking">
                        <PageLoader />
                        <h1 className="font-display text-3xl uppercase tracking-tight mt-6">Confirming your payment</h1>
                        <p className="text-zinc-400 mt-3 text-sm">
                            Hold on a moment — we are waiting for the bank to confirm. Do not close this window.
                        </p>
                    </div>
                )}

                {state === "paid" && (
                    <div data-testid="payment-paid">
                        <CheckCircle2 className="w-12 h-12 text-lime mx-auto" />
                        <h1 className="font-display text-3xl uppercase tracking-tight mt-5">Payment received</h1>
                        <p className="text-zinc-400 mt-3 text-sm">Taking you to your order {orderNumber}…</p>
                    </div>
                )}

                {state === "pending" && (
                    <div data-testid="payment-pending">
                        <RefreshCw className="w-11 h-11 text-gold mx-auto" />
                        <h1 className="font-display text-3xl uppercase tracking-tight mt-5">Still confirming</h1>
                        <p className="text-zinc-400 mt-3 text-sm">
                            Your bank is taking longer than usual for order <b className="text-white">{orderNumber}</b>.
                            We will email you the moment it clears — no need to pay again.
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center mt-7">
                            <button onClick={() => { tries.current = 0; setState("checking"); check(); }}
                                data-testid="payment-recheck" className="btn-pill btn-secondary">CHECK AGAIN</button>
                            <Link to={`/order/${orderNumber}`} className="btn-pill btn-primary"
                                data-testid="payment-view-order">VIEW ORDER</Link>
                        </div>
                    </div>
                )}

                {state === "failed" && (
                    <div data-testid="payment-failed">
                        <XCircle className="w-12 h-12 text-red-400 mx-auto" />
                        <h1 className="font-display text-3xl uppercase tracking-tight mt-5">Payment not completed</h1>
                        <p className="text-zinc-400 mt-3 text-sm">
                            Nothing has been charged. Your order {orderNumber ? <b className="text-white">{orderNumber}</b> : ""} is
                            saved, so you can finish the payment right away.
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center mt-7">
                            <button onClick={retry} disabled={retrying || !orderNumber}
                                data-testid="payment-retry" className="btn-pill btn-primary disabled:opacity-50">
                                {retrying ? "OPENING…" : "TRY PAYMENT AGAIN"}
                            </button>
                            <Link to="/shop" className="btn-pill btn-secondary" data-testid="payment-back-shop">BACK TO SHOP</Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
